import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Button, Form, Modal, Row, Col } from 'react-bootstrap';
import { BsPlusLg as PlusLg, BsPlay as Play, BsPencil, BsStopCircle as Pause, BsTrash, BsUpload } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { TableCSS } from '../styles/MembersStyled';
import { getTeamIdFromURL } from '@/_helpers';
import {
    createPresetAction,
    deletePresetAction,
    fetchPresetsAction,
    updatePresetAction,
    fetchVerticalsAction,
    fetchCategoriesAction,
} from '@/_redux/actions/ActionCallCenter';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Swal from 'sweetalert2';
import AudioRecordModal from '@/components/Features/Common/ChatInputElements/AudioRecordModal';
import { uploadFile } from '@/_redux';
import { MdDelete } from 'react-icons/md';

const getSortedItems = (items = []) => items.sort((a, b) => a?.title?.localeCompare(b?.name));
const DEFAULT_CATEGORY = 'Value Statements';
const ItemRow = ({ sn, item, onEdit, onDelete, speakingPresetId, onPreview, onAudioPreview }) => (
    <tr key={item?._id}>
        <td>{sn}.</td>
        <td>{item?.title}</td>
        <td>{item?.introScript}</td>
        <td>{(item?.verticalData || {})?.name || '-'}</td>
        <td>{(item?.categoryData || {})?.name || '-'}</td>
        <td>
            <div className="d-flex gap-2">
                <ActionStyles>
                    <BootstrapToolTip text="Preview TTS (Robotic Voice)">
                        {speakingPresetId === item?._id ? (
                            <Pause className="icon " onClick={() => onPreview(item.introScript, item._id)} />
                        ) : (
                            <span className="icon " onClick={() => onPreview(item.introScript, item._id)}>
                                🤖
                            </span>
                        )}
                    </BootstrapToolTip>
                    <BootstrapToolTip text="Preview Audio Recording">
                        <Play
                            className={`icon ${item.audioUrl ? '' : 'disabled'}`}
                            onClick={() => onAudioPreview(item.audioUrl, item._id)}
                        />
                    </BootstrapToolTip>
                </ActionStyles>
                <ActionStyles>
                    <BootstrapToolTip text="Edit">
                        <BsPencil className="icon edit" onClick={() => onEdit(item)} />
                    </BootstrapToolTip>
                    <BootstrapToolTip text="Delete">
                        <BsTrash className="icon delete" onClick={() => onDelete(item)} />
                    </BootstrapToolTip>
                </ActionStyles>
            </div>
        </td>
    </tr>
);

const Component = ({
    presets,
    createPresetAction,
    fetchPresetsAction,
    updatePresetAction,
    deletePresetAction,
    fetchVerticalsAction,
    fetchCategoriesAction,
    categories,
    verticals,
    onMutate,
}) => {
    const teamId = getTeamIdFromURL();
    const [isBusy, setIsBusy] = useState(false);
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [verticalFilter, setVerticalFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const fileInputRef = useRef(null);

    const [showPresetModal, setShowPresetModal] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);
    const [presetData, setPresetData] = useState({
        title: '',
        introScript: '',
        aiPrompt: '',
        languageCode: 'en-US',
        sttVendor: '',
        ttsVendor: '',
        vertical: '',
        category: '',
        keywords: '',
    });

    const [isSpeaking, setIsSpeaking] = useState(false);
    const [speakingPresetId, setSpeakingPresetId] = useState(null);
    const [file, setFile] = useState(null);
    const [playAudioUrl, setPlayAudioUrl] = useState(null);
    const getTeamPresets = async () => {
        try {
            const _items = presets;
            setItems(_items);
            applyFilters(_items, searchText, verticalFilter, categoryFilter);
            fetchVerticalsAction(teamId);
            fetchCategoriesAction(teamId);
        } catch (error) {
            if (error?.name !== 'AbortError') toast.error(error.message);
        }
    };

    useEffect(() => {
        getTeamPresets();
    }, [presets]);

    const applyFilters = (itemsToFilter, search, vertical, category) => {
        let filtered = [...itemsToFilter];

        if (search.trim()) {
            filtered = filtered.filter(
                (p) =>
                    p.title?.toLowerCase().includes(search.toLowerCase()) ||
                    p.introScript?.toLowerCase().includes(search.toLowerCase())
            );
        }

        if (vertical) {
            filtered = filtered.filter((p) => p.vertical === vertical);
        }

        if (category) {
            filtered = filtered.filter((p) => {
                if (!p.category && category === DEFAULT_CATEGORY) {
                    return true;
                }
                return p.category === category;
            });
        }

        setFilteredItems(getSortedItems(filtered));
    };

    const handleSearchChange = (e) => {
        const value = e.target.value || '';
        setSearchText(value);
        applyFilters(items, value, verticalFilter, categoryFilter);
    };

    const handleVerticalFilterChange = (e) => {
        const value = e.target.value;
        setVerticalFilter(value);
        applyFilters(items, searchText, value, categoryFilter);
    };

    const handleCategoryFilterChange = (e) => {
        const value = e.target.value;
        setCategoryFilter(value);
        applyFilters(items, searchText, verticalFilter, value);
    };

    const stopSpeak = () => {
        if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setSpeakingPresetId(null);
    };

    const previewSpeak = (text, presetId = null) => {
        try {
            if (!text) return toast.warn('No script to preview');

            if (isSpeaking && speakingPresetId === presetId) {
                stopSpeak();
                return;
            }

            stopSpeak();
            const utter = new SpeechSynthesisUtterance(text);
            utter.onend = stopSpeak;
            window.speechSynthesis.speak(utter);
            setIsSpeaking(true);
            setSpeakingPresetId(presetId);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const previewAudio = (audioUrl) => {
        try {
            if (!audioUrl) return toast.warn('No audio to preview');
            setPlayAudioUrl(audioUrl);
        } catch (e) {
            toast.error(e.message);
        }
    };

    useEffect(
        () => () => {
            if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
        },
        []
    );

    const openCreateModal = () => {
        setEditingPreset(null);
        setPresetData({
            title: '',
            introScript: '',
            aiPrompt: '',
            languageCode: 'en-US',
            sttVendor: '',
            ttsVendor: '',
            vertical: '',
            category: '',
            keywords: '',
        });
        setShowPresetModal(true);
    };

    const openEditModal = (preset) => {
        setEditingPreset(preset);
        setPresetData({
            title: preset.title || '',
            introScript: preset.introScript || '',
            aiPrompt: preset.aiPrompt || '',
            languageCode: preset.languageCode || 'en-US',
            sttVendor: preset.sttVendor || '',
            ttsVendor: preset.ttsVendor || '',
            vertical: preset.vertical || '',
            category: preset.category || '',
            audioUrl: preset.audioUrl || '',
            priority: preset.priority || '',
            keywords: preset.keywords || '',
        });
        if (preset.audioUrl) {
            setFile(false);
        }
        setShowPresetModal(true);
    };

    const handleSavePreset = async () => {
        let url;
        setIsBusy(true);
        const payload = {
            title: presetData.title,
            introScript: presetData.introScript,
            aiPrompt: presetData.aiPrompt,
            languageCode: presetData.languageCode,
            sttVendor: presetData.sttVendor,
            ttsVendor: presetData.ttsVendor,
            vertical: presetData.vertical,
            category: presetData.category,
            audioUrl: presetData.audioUrl,
            priority: presetData.priority,
            keywords: presetData.keywords,
        };
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const response = await uploadFile(formData, `preset/${teamId || 'teamId'}`);
            if (response && response.length) {
                url = response[0].url;
            }
        }
        if (url) {
            payload.audioUrl = url;
        } else if (file === null) {
            payload.audioUrl = '';
        }
        try {
            if (editingPreset) {
                await updatePresetAction(editingPreset._id, payload);
            } else {
                payload.team = teamId;
                await createPresetAction(payload);
            }
            setShowPresetModal(false);
            setFile(null);
            if (onMutate) {
                onMutate();
            } else {
                await fetchPresetsAction(teamId);
            }
        } catch (err) {
            console.log('handlepresetsave', err);
        } finally {
            setIsBusy(false);
        }
    };
    const handleDeletePreset = async (preset) => {
        try {
            const result = await Swal.fire({
                title: `Are you sure you want to delete ?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
            });

            if (result.isConfirmed) {
                await deletePresetAction(preset._id);
                if (onMutate) {
                    onMutate();
                } else {
                    await fetchPresetsAction(teamId);
                }
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to delete preset');
        }
    };

    const handleVoiceRecording = (audioFile) => {
        const maxFileSize = 48; // 50 MB limit
        if (audioFile && audioFile.size < maxFileSize * 1024 * 1024) {
            setFile(audioFile);
        } else {
            toast.error(`Audio file must be less than ${maxFileSize}mb`);
        }
    };

    const handleUploadAudio = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        // Check file type
        const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'];
        if (!allowedTypes.includes(selectedFile.type)) {
            toast.error('Please upload a valid audio file (MP3, WAV, or OGG)');
            return;
        }

        // Check file size (5MB limit)
        const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
        if (selectedFile.size > maxFileSize) {
            toast.error(`Audio file must be less than 5MB`);
            return;
        }

        setFile(selectedFile);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleTriggerUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="presets-tab">
            {/* Filters and Search in Single Row */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Search Input */}
                    <input
                        onChange={handleSearchChange}
                        value={searchText}
                        placeholder="Search presets..."
                        className="form-control"
                        style={{
                            width: '250px',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            padding: '6px 10px',
                        }}
                    />

                    {/* Vertical Filter */}
                    <div style={{ minWidth: '156px' }} className="ml-3">
                        <Form.Group className="mb-0">
                            <Form.Control
                                as="select"
                                value={verticalFilter}
                                onChange={handleVerticalFilterChange}
                                size="sm"
                            >
                                <option value="">All Verticals</option>
                                {verticals.map((item) => (
                                    <option key={item?._id} value={item?._id}>
                                        {item?.name || ''}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </div>

                    {/* Category Filter */}
                    <div style={{ minWidth: '156px' }} className="ml-3">
                        <Form.Group className="mb-0">
                            <Form.Control
                                as="select"
                                value={categoryFilter}
                                onChange={handleCategoryFilterChange}
                                size="sm"
                            >
                                <option value="">All Categories</option>
                                {categories.map((item) => (
                                    <option key={item._id} value={item._id}>
                                        {item.name || ''}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </div>
                </div>

                {/* New Preset Button */}
                <Button variant="primary" onClick={openCreateModal} className="flex-shrink-0">
                    <PlusLg className="me-1" /> New Preset
                </Button>
            </div>

            {/* Table */}
            {!filteredItems ? (
                <div className="text-center py-5">
                    <VscLoading className="spin" fontSize="42" />
                </div>
            ) : (
                <div className="custom-card mt-3">
                    <TableCSS>
                        <thead>
                            <tr>
                                <th style={{ width: 10 }}>S.N.</th>
                                <th>Title</th>
                                <th>Script</th>
                                <th style={{ width: 100 }}>Vertical</th>
                                <th style={{ width: 200 }}>Category</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item, index) => (
                                    <ItemRow
                                        sn={index + 1}
                                        key={item._id}
                                        item={item}
                                        onEdit={openEditModal}
                                        onDelete={handleDeletePreset}
                                        onPreview={previewSpeak}
                                        onAudioPreview={previewAudio}
                                        speakingPresetId={speakingPresetId}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="100">
                                        <div className="text-center py-4">
                                            <h3 className="text-muted mb-0">No Presets Found</h3>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCSS>
                </div>
            )}
            {playAudioUrl && (
                <Modal show={!!playAudioUrl} onHide={() => setPlayAudioUrl(null)} size="lg" centered>
                    <div>
                        <audio controls className="w-100">
                            <source src={playAudioUrl} type="audio/mp3" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </Modal>
            )}

            {/* Modal */}
            <Modal
                show={showPresetModal}
                onHide={() => {
                    setShowPresetModal(false);
                    fileInputRef.current.value = '';
                    setFile(null);
                }}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{editingPreset ? 'Edit Preset' : 'New Preset'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            value={presetData.title}
                            onChange={(e) => setPresetData({ ...presetData, title: e.target.value })}
                            placeholder="Enter preset title"
                        />
                    </Form.Group>

                    {/* Vertical and Category in Modal */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Vertical</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={presetData.vertical}
                                    onChange={(e) => setPresetData({ ...presetData, vertical: e.target.value })}
                                >
                                    <option value="">Select Vertical</option>
                                    {verticals.map((item) => (
                                        <option key={item?._id} value={item?._id}>
                                            {item?.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Category</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={presetData.category}
                                    onChange={(e) => setPresetData({ ...presetData, category: e.target.value })}
                                >
                                    <option value="">Select Category</option>
                                    {categories.map((item) => (
                                        <option key={item?._id} value={item?._id}>
                                            {item?.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Priority</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={presetData.priority || ''}
                                    onChange={(e) => setPresetData({ ...presetData, priority: e.target.value })}
                                >
                                    <option value="">Select Priority</option>
                                    {['low', 'medium', 'high'].map((name) => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Keywords (comma-separated)</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="objection, pricing, benefits, closing..."
                            value={presetData.keywords}
                            onChange={(e) => setPresetData({ ...presetData, keywords: e.target.value })}
                        />
                        <Form.Text className="text-muted">
                            Add relevant keywords to help with AI classification and search
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center justify-content-between">Script</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={presetData.introScript}
                            onChange={(e) => setPresetData({ ...presetData, introScript: e.target.value })}
                            placeholder="Hello, this is ..."
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={() => previewSpeak(presetData.introScript)}
                            className="mt-2"
                        >
                            🤖 Preview Script
                        </Button>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center justify-content-between">
                            Voice Recording
                        </Form.Label>
                    </Form.Group>
                    <div className="d-flex flex-row align-items-center">
                        {file || (presetData || {})?.audioUrl ? (
                            <Button
                                variant="outline-danger"
                                className="mb-2 ml-2"
                                onClick={() => {
                                    setFile(null);
                                    setPresetData({ ...presetData, audioUrl: undefined });
                                }}
                            >
                                <MdDelete fontSize="22px" />
                                Delete Voice
                            </Button>
                        ) : (
                            <div className="d-flex">
                                <AudioRecordModal
                                    onRecordingComplete={handleVoiceRecording}
                                    maxDuration={60} // 1 minutes max
                                    fileName="call-center-audio-web.mp3"
                                    compact={true}
                                    buttonMode={true}
                                />
                                <Button
                                    variant="outline-primary"
                                    onClick={handleTriggerUpload}
                                    className="d-flex align-items-center ml-2  mb-2"
                                >
                                    <BsUpload className="mr-2" /> Upload Audio
                                </Button>
                            </div>
                        )}
                        {/* Hidden file input for upload */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="audio/mp3"
                            onChange={handleUploadAudio}
                        />
                        <div>
                            {!file && !(presetData || {})?.audioUrl && (
                                <Form.Text className="text-muted ml-2">
                                    No custom audio uploaded; this preset will use text-to-speech during calls.
                                </Form.Text>
                            )}
                        </div>
                    </div>
                    {(file || (presetData || {})?.audioUrl) && (
                        <div>
                            <audio controls className="w-100">
                                <source
                                    src={file ? URL.createObjectURL(file) : (presetData || {})?.audioUrl}
                                    type="audio/mp3"
                                />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPresetModal(false)} disabled={isBusy}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSavePreset} disabled={isBusy}>
                        {isBusy && <VscLoading className="spin mr-2" />}
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
const mapStateToProps = (state) => ({
    presets: state.callCenter.presets || [],
    verticals: state.callCenter.verticals || [],
    categories: state.callCenter.categories || [],
});
const mapDispatchToProps = {
    fetchPresetsAction,
    createPresetAction,
    updatePresetAction,
    deletePresetAction,
    fetchVerticalsAction,
    fetchCategoriesAction,
};

const PresetsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(PresetsTab);
